import { useState } from 'react'
import { Sun, LayoutGrid, Compass, Menu, Plus, MessageCircle, ListTodo, X } from 'lucide-react'
import { BRAND } from '../../../config/brand'
import { useLocation, useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { useBranding } from '../../../hooks/useBranding'

interface Tab {
  label: string
  icon: typeof Sun
  href?: string
  match?: string[]
  action?: 'drawer' | 'capture'
}

/** Three Rooms shell (owner pick 2026-08-25): destination tabs replace the
 *  old group tabs (Now/Vision/Build/Execute) — one tap to any room, and the
 *  center ＋ is quick capture (tell the compass, or add a task). */
const tabs: Tab[] = [
  { label: 'Today', icon: Sun, href: '/today', match: ['/today'] },
  {
    label: 'Map', icon: LayoutGrid, href: '/life-map',
    match: ['/life-map', '/dreams', '/goals', '/habits', '/skills', '/tasks',
            '/my-people', '/places'],
  },
  { label: 'Add', icon: Plus, action: 'capture' },
  {
    label: BRAND.assistant, icon: Compass, href: '/horton-ai',
    match: ['/horton-ai', '/diary'],
  },
  { label: 'More', icon: Menu, action: 'drawer' },
]

interface MobileBottomNavProps {
  onMoreClick: () => void
}

export function MobileBottomNav({ onMoreClick }: MobileBottomNavProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const branding = useBranding()
  const [captureOpen, setCaptureOpen] = useState(false)

  const isActive = (tab: Tab) => {
    const prefixes = tab.match ?? (tab.href ? [tab.href] : [])
    return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))
  }

  const handleTap = async (tab: Tab) => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
        Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
      } catch { /* haptics unavailable */ }
    }
    if (tab.action === 'drawer') {
      setCaptureOpen(false)
      onMoreClick()
    } else if (tab.action === 'capture') {
      setCaptureOpen((v) => !v)
    } else if (tab.href) {
      setCaptureOpen(false)
      navigate(tab.href)
    }
  }

  const capture = (href: string) => {
    setCaptureOpen(false)
    navigate(href)
  }

  return (
    <>
      {/* Quick-capture sheet, anchored above the bar */}
      {captureOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            aria-hidden="true"
            onClick={() => setCaptureOpen(false)}
          />
          <div className="fixed bottom-[calc(76px+env(safe-area-inset-bottom,0px))] left-1/2 z-50 w-64 -translate-x-1/2 rounded-2xl border border-soft bg-surface p-2 shadow-[0_10px_28px_rgba(34,37,43,0.25)]">
            <button
              type="button"
              onClick={() => capture('/horton-ai')}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-primary hover:bg-surface-alt"
            >
              <MessageCircle className="h-4 w-4 text-brand" />
              {/* 2026-08-29: the persona name was hardcoded, so the default
                  label used to change with the theme. */}
              Tell {branding.assistant}
            </button>
            <button
              type="button"
              onClick={() => capture('/tasks/create')}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-primary hover:bg-surface-alt"
            >
              <ListTodo className="h-4 w-4 text-brand" />
              New task
            </button>
          </div>
        </>
      )}

      <nav
        className="sticky bottom-0 z-40 flex items-stretch justify-around border-t border-soft bg-surface keyboard-hide"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Main navigation"
      >
        {tabs.map((tab) => {
          const active = isActive(tab)
          if (tab.action === 'capture') {
            return (
              <button
                key={tab.label}
                onClick={() => handleTap(tab)}
                aria-label={captureOpen ? 'Close quick add' : 'Quick add'}
                aria-expanded={captureOpen}
                className="flex flex-1 items-center justify-center"
              >
                <span className="-mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-brand text-on-brand shadow-[0_6px_14px_rgba(120,81,169,0.4)] transition-transform active:scale-95">
                  {captureOpen
                    ? <X className="h-5 w-5" />
                    : <Plus className="h-6 w-6" />}
                </span>
              </button>
            )
          }
          return (
            <button
              key={tab.label}
              onClick={() => handleTap(tab)}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-1 flex-col items-center justify-center gap-0.5 min-h-12 py-2.5 transition-colors ${
                active ? 'text-brand' : 'text-muted active:text-primary'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className={`text-app-micro ${active ? 'font-bold' : 'font-medium'}`}>{tab.label}</span>
            </button>
          )
        })}
      </nav>
    </>
  )
}
